import React, { useState } from 'react';
import { Menu, Phone, Mail, ChevronDown, Droplets, WrenchIcon, ThermometerSun, Construction, HeartPulse } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToSection } from '../utils/scroll';
import AboutUsModal from './AboutUsModal';

const services = [
  {
    title: 'Pool Maintenance',
    description: 'Keep your pool in perfect condition year-round with professional maintenance packages.',
    slug: 'pool-maintenance',
    icon: Droplets,
    color: 'from-blue-500/10 to-cyan-500/10'
  },
  {
    title: 'Repairs & Equipment',
    description: 'Expert repair services for all pool equipment and systems.',
    slug: 'repairs-equipment',
    icon: WrenchIcon,
    color: 'from-orange-500/10 to-red-500/10'
  },
  {
    title: 'Heat Pumps',
    description: 'Energy-efficient pool heating solutions for year-round swimming.',
    slug: 'heat-pumps',
    icon: ThermometerSun,
    color: 'from-yellow-500/10 to-orange-500/10'
  },
  {
    title: 'New Pool Installation',
    description: 'Custom pool design and construction tailored to your space and style.',
    slug: 'new-pool-installation',
    icon: Construction,
    color: 'from-emerald-500/10 to-green-500/10'
  },
  {
    title: 'Water Care',
    description: 'Professional water testing and treatment for crystal-clear pools.',
    slug: 'water-care',
    icon: HeartPulse,
    color: 'from-purple-500/10 to-pink-500/10'
  }
];

const Header = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const location = useLocation();
  const [logoIndex, setLogoIndex] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);
  const logoCandidates = ['/logo.png', '/logo.jpg', '/logo.jpeg', '/logo.svg'];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // If not on home page, navigate home first
      window.location.href = `/#${sectionId}`;
    } else {
      scrollToSection(sectionId);
    }
    setIsServicesOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <header className="bg-white shadow-md transition-shadow duration-300 sticky top-0 z-40">
      <AboutUsModal 
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link 
              to="/"
              onClick={handleLogoClick}
              className="flex items-center text-cyan-600 hover:text-cyan-700 transition-all duration-300 hover:scale-105 group"
            >
              {!logoFailed && (
                <img
                  src={logoCandidates[logoIndex]}
                  alt="Aqua Blue Cyprus"
                  className="h-12 w-auto object-contain"
                  loading="eager"
                  decoding="async"
                  onError={() => {
                    setLogoIndex((i) => {
                      const next = i + 1;
                      if (next < logoCandidates.length) return next;
                      setLogoFailed(true);
                      return i;
                    });
                  }}
                />
              )}
              
              <div className="ml-3 leading-none">
                <span className="block text-2xl font-bold bg-gradient-to-r from-sky-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent tracking-tight">AQUA BLUE</span>
                <span className="text-sm tracking-[0.25em] text-gray-500 font-medium">CYPRUS</span>
              </div>
            </Link>
          </div>
          
          <nav className="hidden md:flex space-x-8">
            <div className="relative group">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(e as any, 'services');
                  setIsServicesOpen(!isServicesOpen);
                }}
                onMouseEnter={() => setIsServicesOpen(true)}
                className="flex items-center text-gray-700 hover:text-cyan-600 transition-all duration-300"
              >
                Services
                <ChevronDown className={`h-4 w-4 ml-1 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isServicesOpen && (
                <div
                  className="absolute top-full left-0 w-[600px] bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl mt-2 p-4 z-50 transition-all duration-300 animate-fadeIn border border-gray-100"
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <div className="grid gap-3">
                    {services.map((service) => {
                      const Icon = service.icon;
                      return (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          className="relative group rounded-xl p-4 transition-all duration-300 hover:translate-x-1 overflow-hidden"
                          onClick={() => setIsServicesOpen(false)}
                        >
                          {/* Background gradient that follows mouse */}
                          <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r ${service.color} transition-opacity duration-300`} />
                          
                          <div className="relative flex items-start gap-4">
                            <div className="flex-shrink-0 p-2 rounded-lg bg-white shadow-md group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                              <Icon className="w-6 h-6 text-cyan-600" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 group-hover:text-cyan-700 transition-colors duration-300">
                                {service.title}
                              </div>
                              <div className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                                {service.description}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={() => setShowAboutModal(true)}
              className="text-gray-700 hover:text-cyan-600 transition-all duration-300 hover:translate-y-[-2px]"
            >
              About
            </button>
            <a 
              href="#gallery" 
              onClick={(e) => handleNavClick(e, 'gallery')}
              className="text-gray-700 hover:text-cyan-600 transition-all duration-300 hover:translate-y-[-2px]"
            >
              Gallery
            </a>
            <a 
              href="#contact" 
              onClick={(e) => handleNavClick(e, 'contact')}
              className="text-gray-700 hover:text-cyan-600 transition-all duration-300 hover:translate-y-[-2px]"
            >
              Contact
            </a>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+35799562552"
              className="flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition-colors duration-300"
            >
              <Phone className="h-5 w-5" />
              <span>+357 99 562 552</span>
            </a>
            <a 
              href="mailto:info@aquabluecyprus.com" 
              className="flex items-center text-gray-700 hover:text-cyan-600 transition-all duration-300 hover:scale-105"
            >
              <Mail className="h-5 w-5 mr-1 transition-transform duration-300 group-hover:rotate-12" />
              <span>info@aquabluecyprus.com</span>
            </a>
          </div>

          <button
            className="md:hidden transition-transform duration-300 hover:scale-110 focus:outline-none"
            aria-label="Open menu"
            onClick={() => setMobileOpen(v => !v)}
          >
            <Menu className="h-6 w-6 text-gray-700 transition-colors duration-300 hover:text-cyan-600" />
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white shadow-lg">
          <nav className="px-4 py-3 space-y-2">
            <a
              href="#services"
              onClick={(e) => { handleNavClick(e, 'services'); setMobileOpen(false); }}
              className="block py-2 text-gray-700 hover:text-cyan-600"
            >
              Services
            </a>
            <button
              onClick={() => { setShowAboutModal(true); setMobileOpen(false); }}
              className="block w-full text-left py-2 text-gray-700 hover:text-cyan-600"
            >
              About
            </button>
            <a
              href="#gallery"
              onClick={(e) => { handleNavClick(e, 'gallery'); setMobileOpen(false); }}
              className="block py-2 text-gray-700 hover:text-cyan-600"
            >
              Gallery
            </a>
            <a
              href="#contact"
              onClick={(e) => { handleNavClick(e, 'contact'); setMobileOpen(false); }}
              className="block py-2 text-gray-700 hover:text-cyan-600"
            >
              Contact
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;